import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";

interface TodayChatResponse {
	chatId: string;
	messages: Array<{
		id: string;
		role: string;
		content: string | Array<{ text?: string }>;
		createdAt: string;
	}>;
	isNew: boolean;
}

export function useTodayChat(options?: {
	forceNew?: boolean;
	enabled?: boolean;
}) {
	const { user: auth0User } = useAuth0();
	const forceNew = options?.forceNew ?? false;
	const enabled = options?.enabled ?? true;

	return useQuery<TodayChatResponse>({
		// The Auth0 sub is part of the key because the response is per-user and
		// this cache outlives a switch between accounts: web logout wipes it via
		// a full reload, but a native (Capacitor) re-auth keeps the same SPA
		// session alive. Without it, the next account reads the previous one's
		// chatId and posts into it — era-api answers that with 403 (#212).
		// Invalidations keyed on ["chat", "today"] still match by prefix.
		queryKey: ["chat", "today", auth0User?.sub ?? "anonymous", forceNew],
		queryFn: async () => {
			const url = forceNew
				? "/api/chat/today?forceNew=true"
				: "/api/chat/today";
			const response = await api.get(url);
			return response.data;
		},
		enabled: !!auth0User && enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
	});
}

export type { TodayChatResponse };
